import { useTranslation } from 'react-i18next';
import type { SmartValue } from '@optiflow/types';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { CountText, DaysText } from '../components/ReportBlocks';
import { MoneyByCurrencyText } from '../components/MoneyByCurrencyText';

/**
 * One Smart Report value, drawn as what it is: money one line per currency, never summed; a
 * quantity one line per product type, never summed; a duration in days; messages with their
 * automated and manual split.
 */
export function SmartValueText({ value }: { value: SmartValue }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  switch (value.kind) {
    case 'MONEY':
      return <MoneyByCurrencyText amounts={value.amounts} />;
    case 'AVERAGE_MONEY':
      return (
        <span className="flex flex-col">
          {value.amounts.map((entry, index) => (
            <span
              key={entry.currencyCode}
              dir="ltr"
              className={index > 0 ? 'text-muted text-sm' : undefined}
            >
              {entry.amountMinor === null
                ? '—'
                : formatMoney(entry.amountMinor, locale, entry.currencyCode)}
            </span>
          ))}
        </span>
      );
    case 'COUNT':
      return <CountText value={value.value} />;
    case 'DURATION':
      return <DaysText seconds={value.seconds} />;
    case 'QUANTITY_BY_TYPE':
      if (value.quantities.length === 0) return <CountText value={0} />;
      return (
        <span className="flex flex-col gap-0.5 text-base font-normal">
          {value.quantities.map((entry) => (
            <span key={entry.productType ?? 'none'}>
              {entry.productType
                ? t(`catalog.types.${entry.productType}`)
                : t('reports.smart.unattributed')}
              {': '}
              <CountText value={entry.quantity} />
            </span>
          ))}
        </span>
      );
    case 'MODE_COUNTS':
      return (
        <span className="flex flex-col">
          <CountText value={value.total} />
          <span className="text-muted text-xs font-normal">
            {t('reports.smart.modeSplit', {
              automated: new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-GB').format(
                value.automated,
              ),
              manual: new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-GB').format(
                value.manual,
              ),
            })}
          </span>
        </span>
      );
  }
}
